<script setup lang="ts">
import NewDateRangePicker from '@/components/Globals/Inputs/NewDateRangePicker.vue'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import InputGroup from '@/components/Auths/InputGroup.vue'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcSearch from '@/components/Icons/IcSearch.vue'
import AdvancedFiltersModal from './AdvancedFiltersModal.vue'
import { formatPeriodDate } from '@/utils/date/date-time'
import { useSensorStore } from '@/store/sensor'
import { storeToRefs } from 'pinia'
import { Form, Field } from 'vee-validate'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMaintenanceStore } from '@/store/maintenance'
import { IMaintenance } from '@/interfaces/entities/maintenance'
import { useCompanyStore } from '@/store/company'

const { t } = useI18n()

const isLoading = ref(false)
const formRef = ref<InstanceType<typeof Form>>()
const dropdownResetKey = ref(0)
const { listSensors } = storeToRefs(useSensorStore())
const { listMaintenanceTypes } = storeToRefs(useMaintenanceStore())
const { getCurrentMaintenanceTypeFilter, getCurrentPriorityFilter, getCurrentOrigimFilter, getCurrentSensorFilter, getCurrentUserFilter, getCurrentStatusFilter } = storeToRefs(
  useMaintenanceStore(),
)
const {
  setCurrentMaintenanceTypeFilter,
  setCurrentEndDateFilter,
  setCurrentStartDateFilter,
  setCurrentActionsFilter,
  setCurrentDescriptionFilter,
  setCurrentPriorityFilter,
  setCurrentOrigimFilter,
  setCurrentStatusFilter,
  setCurrentSensorFilter,
  setCurrentUserFilter,
  getMaintenancesPages,
} = useMaintenanceStore()
const { fetch: fetchSensors } = useSensorStore()
const { getMaintenanceTypes } = useMaintenanceStore()
const { fetchCompanyUsers } = useCompanyStore()
const { getCompanyUsers } = storeToRefs(useCompanyStore())

const showFiltersModal = ref(false)
const visibleExtraFilters = ref({
  description: false,
  origin: false,
  maintenance_type: false,
  actions_taken: false,
  scheduled_period: false,
})

const clearFilters = () => {
  formRef.value?.resetForm()
  setCurrentSensorFilter(null)
  setCurrentUserFilter(null)
  setCurrentStatusFilter(null)
  setCurrentOrigimFilter(null)
  setCurrentPriorityFilter(null)
  setCurrentDescriptionFilter(null)
  setCurrentActionsFilter(null)
  setCurrentMaintenanceTypeFilter(null)
  setCurrentStartDateFilter(null)
  setCurrentEndDateFilter(null)
  dropdownResetKey.value++

  Object.keys(visibleExtraFilters.value).forEach((key) => {
    visibleExtraFilters.value[key] = false
  })

  getMaintenancesPages(1, 10, 'id', 'desc')
}

const statusOptions = ref([
  { id: 'open', name: t('maintenance_listing.header.status.open') },
  { id: 'in_progress', name: t('maintenance_listing.header.status.in_progress') },
  { id: 'done', name: t('maintenance_listing.header.status.done') },
  { id: 'canceled', name: t('maintenance_listing.header.status.canceled') },
])

const originOptions = ref([
  { id: 'manual', name: t('maintenance_listing.header.origin.manual') },
  { id: 'alarm', name: t('maintenance_listing.header.origin.alarm') },
  { id: 'schedule', name: t('maintenance_listing.header.origin.schedule') },
  { id: 'inspection', name: t('maintenance_listing.header.origin.inspection') },
])

const priorityOptions = ref([
  { id: 'low', name: t('maintenance_listing.header.priority.low') },
  { id: 'medium', name: t('maintenance_listing.header.priority.medium') },
  { id: 'high', name: t('maintenance_listing.header.priority.high') },
])

const onSubmit = async (values: object) => {
  const castedValues = values as IMaintenance
  let formattedPeriod = null
  isLoading.value = true
  try {
    setCurrentSensorFilter(castedValues.sensor)
    setCurrentUserFilter(castedValues.user)
    setCurrentStatusFilter(castedValues.status)
    setCurrentOrigimFilter(castedValues.origin)
    setCurrentPriorityFilter(castedValues.priority)
    setCurrentDescriptionFilter(castedValues.description)
    setCurrentActionsFilter(castedValues.actions_taken)
    setCurrentMaintenanceTypeFilter(castedValues.maintenance_type)

    if (castedValues.period && typeof castedValues.period !== 'undefined') {
      try {
        formattedPeriod = formatPeriodDate(castedValues.period)
      } catch {
        formattedPeriod = null
      }
    }

    if (formattedPeriod && typeof formattedPeriod === 'string') {
      const [startDate, endDate] = formattedPeriod.split('&').map((part) => part.split('=')[1])
      setCurrentStartDateFilter(startDate)
      setCurrentEndDateFilter(endDate)
    } else {
      setCurrentStartDateFilter(null)
      setCurrentEndDateFilter(null)
    }

    await getMaintenancesPages(1, 10, 'id', 'desc')
  } catch (error) {
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchSensors()
  fetchCompanyUsers()
  getMaintenanceTypes()
})
</script>

<template>
  <div class="mt-4 bg-white p-4 shadow-md">
    <Form ref="formRef" @submit="onSubmit" class="flex w-full flex-col gap-4">
      <div class="flex flex-wrap gap-3">
        <SearchBarField :label="t('maintenance_listing.body.table.columns.user')" class="min-w-[200px] flex-1">
          <Dropdown
            :key="dropdownResetKey"
            name="user"
            :options="getCompanyUsers"
            :label-attribute="'name'"
            :has-all-option="true"
            :already-selected-option="getCurrentUserFilter"
            class="w-full" />
        </SearchBarField>

        <SearchBarField :label="t('maintenance_listing.body.table.columns.sensor')" class="min-w-[200px] flex-1">
          <Dropdown
            :key="dropdownResetKey"
            name="sensor"
            :options="listSensors"
            :label-attribute="'name'"
            :has-all-option="true"
            :already-selected-option="getCurrentSensorFilter"
            class="w-full" />
        </SearchBarField>

        <SearchBarField :label="t('maintenance_listing.body.table.columns.status')" class="min-w-[200px] flex-1">
          <Dropdown
            :key="dropdownResetKey"
            name="status"
            :options="statusOptions"
            :label-attribute="'name'"
            :has-all-option="true"
            :already-selected-option="getCurrentStatusFilter"
            class="w-full" />
        </SearchBarField>

        <SearchBarField :label="t('maintenance_listing.body.table.columns.priority')" class="min-w-[200px] flex-1">
          <Dropdown
            :key="dropdownResetKey"
            name="priority"
            :options="priorityOptions"
            :label-attribute="'name'"
            :has-all-option="true"
            :already-selected-option="getCurrentPriorityFilter"
            class="w-full" />
        </SearchBarField>

        <div class="flex min-w-[200px] flex-1 flex-col pt-7">
          <button
            type="button"
            @click="showFiltersModal = true"
            class="flex h-12 items-center justify-center gap-2 border border-gray-300 bg-white px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue focus:ring-offset-2">
            + Filtros Avançados
            <span class="px-0 py-0 pi pi-filter" style="font-size: 0.75rem;"></span>
          </button>
        </div>
        <AdvancedFiltersModal v-model="showFiltersModal" v-model:visible-filters="visibleExtraFilters" />
        
      </div>

      <div class="flex flex-wrap gap-3">
        <SearchBarField v-if="visibleExtraFilters.origin" :label="t('maintenance_listing.body.table.columns.origin')" class="min-w-[200px] flex-1">
          <Dropdown
            name="origin"
            :options="originOptions"
            :label-attribute="'name'"
            :has-all-option="true"
            :already-selected-option="getCurrentOrigimFilter"
            class="w-full" />
        </SearchBarField>

        <SearchBarField v-if="visibleExtraFilters.maintenance_type" :label="t('maintenance_listing.body.table.columns.maintenance_type')" class="min-w-[200px] flex-1">
          <Dropdown
            name="maintenance_type"
            :options="listMaintenanceTypes"
            :label-attribute="'name'"
            :has-all-option="true"
            :already-selected-option="getCurrentMaintenanceTypeFilter"
            class="w-full" />
        </SearchBarField>
      </div>

      <div class="flex flex-wrap items-end gap-3">

        <div v-if="visibleExtraFilters.description" class="min-w-[200px] flex-1">
          <InputGroup
            name="description"
            :label="t('maintenance_listing.body.table.columns.description')"
            type="text"
            :placeholder="t('maintenance_listing.header.actions.placeholder.description')"
            class="w-full" />
        </div>

        <div v-if="visibleExtraFilters.actions_taken" class="min-w-[200px] flex-1">
          <InputGroup
            name="actions_taken"
            :label="t('maintenance_listing.body.table.columns.actions_taken')"
            type="text"
            :placeholder="t('maintenance_listing.header.actions.placeholder.actions_taken')"
            class="w-full" />
        </div>

        <SearchBarField v-if="visibleExtraFilters.scheduled_period" :label="t('maintenance_listing.body.table.columns.scheduled_for')" class="min-w-[320px] flex-1">
          <Field name="period" v-slot="{ field }">
            <NewDateRangePicker v-bind="field" :placeholder="t('plugins.vue_datepicker.placeholder')" class="w-full" />
          </Field>
        </SearchBarField>

        
      </div>
      <div class="sticky bottom-0 left-0 right-0 bg-white pt-3 border-t border-gray-200 z-2">
        <div class="flex justify-between">

          <button :disabled="isLoading" type="button" @click="clearFilters" class="bg-gray-200 px-4 py-3 text-sm text-gray-700 hover:bg-gray-300">
            <span class="px-0 py-0 pi pi-filter-slash" style="font-size: 0.75rem;"></span>
            {{ t('maintenance_listing.header.buttons.clear') }}
          </button>

          <button :disabled="isLoading" class="flex flex-row items-center justify-center bg-blue px-4 py-3 text-sm text-white hover:bg-blue-2">
            <IcSearch v-if="!isLoading" class="mr-1 h-4 w-4" />
            <span v-else class="mr-1 animate-spin">
              <IcAnimateSpin class="h-4 w-4 text-white" />
            </span>
            {{ t('maintenance_listing.header.buttons.search') }}
          </button>
        </div>
      </div>
    </Form>
  </div>
</template>
